// What happens besides signing, under the project's signing policy (carried
// on the workflow read as `signing_policy`): a signer declines, whoever sent
// the document withdraws it — and how an ended signing reads afterwards.
// Every button shows only when the backend would accept it; the backend still
// decides (and its error code becomes the toast).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, Undo2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePostRequest } from '@/shared/hooks/requests';
import { WORKFLOW_DECLINE, WORKFLOW_WITHDRAW } from '@/shared/utils/urls';
import { field as get, signingState } from '../signingState';
import { participantIdLabel, participantIsMe } from '../participants';
import { WorkflowNotice } from './WorkflowNotice';

const nameOf = (p) =>
  get(p?.signature?.certificate, 'subject_common_name') || get(p, 'full_name') || participantIdLabel(p) || '—';

export function SigningActions({ collection, fileId, isGuest, guestToken, currentUser, document, onChanged }) {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState(null); // 'decline' | 'withdraw' | null
  const [reason, setReason] = useState('');

  const headers = guestToken ? { Authorization: `Bearer ${guestToken}`, 'accept-language': i18n.language } : undefined;
  const decline = usePostRequest({ url: WORKFLOW_DECLINE.replace('{file_id}', fileId), ...(headers ? { headers } : {}) });
  const withdraw = usePostRequest({ url: WORKFLOW_WITHDRAW.replace('{file_id}', fileId), ...(headers ? { headers } : {}) });

  if (!collection) return null;
  const policy = get(collection, 'signing_policy') || {};
  const rule = (key) => get(policy, key);
  const { status, superseded, ended } = signingState(collection);
  const participants = collection.participants || [];
  const declined = participants.filter((p) => p.status === 'rejected');
  const anySigned = participants.some((p) => p.status === 'signed');

  // The caller's pending slot — the server says which (`is_me`).
  const mySlot = participants.find((p) => p.status === 'pending' && participantIsMe(p, { userId: currentUser?.id }));
  const canDecline = Boolean(rule('decline')) && !ended && Boolean(mySlot);
  const reasonRequired = rule('decline_reason') !== 'optional';
  const halts = rule('on_decline') !== 'continue';

  const meId = currentUser?.id != null ? String(currentUser.id) : null;
  const isInitiator = !isGuest && meId != null && String(get(collection, 'initiator_user_id')) === meId;
  const isOwner = !isGuest && meId != null && String(get(document, 'owner_id')) === meId;
  const withdrawMode = rule('withdraw') || 'off';
  const canWithdraw =
    !isGuest &&
    withdrawMode !== 'off' &&
    (status === 'active' || status === 'rejected') &&
    !superseded &&
    (isInitiator || (rule('withdraw_by') === 'initiator_or_owner' && isOwner)) &&
    !(withdrawMode === 'before_first_signature' && anySigned);
  const willUnfreeze = rule('on_withdraw') === 'unfreeze_if_unsigned' && !anySigned;
  const qrStays = rule('withdrawn_verification') !== 'off';

  const close = () => {
    setDialog(null);
    setReason('');
  };

  const submit = async () => {
    const isDecline = dialog === 'decline';
    const req = isDecline ? decline : withdraw;
    const { success, response } = await req.request({ data: { reason: reason.trim() || null } });
    if (!success) return;
    toast.success(t(isDecline ? 'signing:actions.declined' : 'signing:actions.withdrawn'));
    close();
    onChanged?.(response);
  };

  const endedAt = get(collection, 'ended_at');
  const when = endedAt ? new Date(endedAt).toLocaleString(i18n.language) : null;
  const withdrawReason = get(collection, 'withdraw_reason');
  const busy = decline.loading || withdraw.loading;
  const missingReason = dialog === 'decline' && reasonRequired && !reason.trim();

  return (
    <>
      <WorkflowNotice
        collection={collection}
        fileId={fileId}
        currentUser={currentUser}
        isGuest={isGuest}
        guestToken={guestToken}
        onChanged={onChanged}
      />

      {status === 'withdrawn' && (
        <div className="text-sm rounded-md border border-border bg-muted/40 p-3 space-y-1" data-testid="signing-withdrawn">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <Undo2 className="w-4 h-4 shrink-0 text-muted-foreground" />
            {t('signing:actions.withdrawnTitle')}
          </div>
          {when && <div className="text-xs text-muted-foreground">{when}</div>}
          {withdrawReason && <div className="text-xs text-foreground">{t('signing:actions.reasonLine', { reason: withdrawReason })}</div>}
          <div className="text-xs text-muted-foreground">
            {superseded ? t('signing:actions.withdrawnEditable') : t('signing:actions.withdrawnFrozen')}
          </div>
        </div>
      )}

      {status === 'rejected' && (
        <div className="text-sm rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 space-y-1" data-testid="signing-declined">
          <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-400">
            <XCircle className="w-4 h-4 shrink-0" />
            {t('signing:actions.haltedTitle')}
          </div>
          {when && <div className="text-xs text-muted-foreground">{when}</div>}
        </div>
      )}

      {declined.length > 0 && (
        <ul className="space-y-1">
          {declined.map((p) => (
            <li key={p.id} className="text-xs text-foreground">
              <span className="font-medium">{t('signing:actions.declinedBy', { name: nameOf(p) })}</span>
              {get(p, 'decline_reason') && (
                <span className="text-muted-foreground"> {t('signing:actions.reasonLine', { reason: get(p, 'decline_reason') })}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {(canDecline || canWithdraw) && (
        <div className="flex flex-col gap-2">
          {canDecline && (
            <Button variant="outline" size="sm" className="w-full text-red-600 dark:text-red-400 border-red-200 dark:border-red-800" onClick={() => setDialog('decline')}>
              <Ban className="w-4 h-4 mr-2" />
              {t('signing:actions.decline')}
            </Button>
          )}
          {canWithdraw && (
            <Button variant="outline" size="sm" className="w-full" onClick={() => setDialog('withdraw')}>
              <Undo2 className="w-4 h-4 mr-2" />
              {t('signing:actions.withdraw')}
            </Button>
          )}
        </div>
      )}

      <Dialog open={dialog !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="sm:max-w-md" zIndex={200}>
          <DialogHeader>
            <DialogTitle className="text-base">
              {dialog === 'decline' ? t('signing:actions.declineTitle') : t('signing:actions.withdrawTitle')}
            </DialogTitle>
            <DialogDescription>
              {dialog === 'decline'
                ? t(halts ? 'signing:actions.declineHalts' : 'signing:actions.declineContinues')
                : `${t(willUnfreeze ? 'signing:actions.withdrawUnfreezes' : 'signing:actions.withdrawFreezes')} ${t(qrStays ? 'signing:actions.withdrawQrStays' : 'signing:actions.withdrawQrOff')}`}
            </DialogDescription>
          </DialogHeader>
          <label className="text-xs font-medium text-foreground" htmlFor="signing-action-reason">
            {dialog === 'decline' && reasonRequired ? t('signing:actions.reasonRequired') : t('signing:actions.reasonOptional')}
          </label>
          <Textarea
            id="signing-action-reason"
            data-testid="signing-action-reason"
            value={reason}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('signing:actions.reasonPlaceholder')}
          />
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>{t('signing:actions.cancel')}</Button>
            <Button
              variant={dialog === 'decline' ? 'destructive' : 'default'}
              onClick={submit}
              disabled={busy || missingReason}
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dialog === 'decline' ? t('signing:actions.declineConfirm') : t('signing:actions.withdrawConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SigningActions;
